'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Skeleton } from '@common/components';
import { cn } from '@core/utils';
import { type ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ROUTES } from '@/constants';
import { useIsGuest } from '@/hooks/useIsGuest';
import { LUCKY_LOTTO_GROUP_TITLE } from '../_constants';
import { useBenefitMenus } from '../_hooks/useBenefitMenus';
import { useCpcClick } from '../_hooks/useCpcClick';
import { useOpenOfferwall } from '../_hooks/useOpenOfferwall';
import { isLuckyTicketAdItem } from '../_utils/resolveQuickMenuTiles';
import { useLuckyTicketCount } from '../lucky-ticket/_hooks/useLuckyTicketCount';
import { BenefitMenuRow } from './BenefitMenuRow';
import { LuckyLottoCard } from './LuckyLottoCard';
export const BENEFIT_SECTION_CLASS_NAME = 'flex flex-col gap-12 bg-Gray-white p-20';
const LuckyTicketCountBadge = () => {
  const $ = _c(2);
  const {
    data: luckyTicketCount
  } = useLuckyTicketCount();
  if (luckyTicketCount === 0) {
    return null;
  }
  let t0;
  if ($[0] !== luckyTicketCount) {
    t0 = <Badge className="bg-O-25 text-O-300">{luckyTicketCount}개</Badge>;
    $[0] = luckyTicketCount;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const BenefitMenuSection = t0 => {
  const $ = _c(17);
  const {
    isGuest: serverIsGuest,
    className,
    afterLuckyLottoGroup
  } = t0;
  const isGuest = useIsGuest(serverIsGuest);
  const {
    data: listGroups
  } = useBenefitMenus(isGuest);
  const {
    openOfferwall,
    gateModals
  } = useOpenOfferwall();
  const {
    clickCpc,
    gateModals: cpcGateModals
  } = useCpcClick();
  let t1;
  if ($[0] !== className || $[1] !== clickCpc || $[2] !== isGuest || $[3] !== openOfferwall) {
    t1 = group => {
      const items = group.items.filter(_temp);
      if (items.length === 0) {
        return null;
      }
      return <section key={group.title} className={cn(BENEFIT_SECTION_CLASS_NAME, className)}><h2 className="font-700 text-Gray-900 typo-body3">{group.title}</h2>{group.title === LUCKY_LOTTO_GROUP_TITLE ? <LuckyLottoCard isGuest={isGuest} /> : null}<ul className="-mx-12 flex flex-col">{items.map(item_0 => {
            const {
              placementKey,
              benefitMenuId,
              url,
              cpc
            } = item_0;
            const cpcTarget = cpc && benefitMenuId && url ? {
              benefitMenuId,
              url,
              cpc
            } : null;
            return <li key={item_0.title}><BenefitMenuRow imageUrl={item_0.imageUrl} title={item_0.title} description={item_0.description} isNew={item_0.isNew} isPopular={item_0.isPopular} cpc={cpcTarget?.cpc} trailing={!isGuest && url === ROUTES.BENEFIT_SCRATCH.pathname ? <ErrorBoundary fallback={null}><Suspense fallback={null}><LuckyTicketCountBadge /></Suspense></ErrorBoundary> : null} href={cpcTarget ? undefined : url} onSelect={cpcTarget ? () => clickCpc(cpcTarget) : !url && placementKey ? () => openOfferwall(placementKey) : undefined} /></li>;
          })}</ul></section>;
    };
    $[0] = className;
    $[1] = clickCpc;
    $[2] = isGuest;
    $[3] = openOfferwall;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const renderGroup = t1;
  let t2;
  if ($[5] !== listGroups || $[6] !== renderGroup) {
    t2 = listGroups.filter(_temp2).map(renderGroup);
    $[5] = listGroups;
    $[6] = renderGroup;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== listGroups || $[9] !== renderGroup) {
    t3 = listGroups.filter(_temp3).map(renderGroup);
    $[8] = listGroups;
    $[9] = renderGroup;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== afterLuckyLottoGroup || $[12] !== cpcGateModals || $[13] !== gateModals || $[14] !== t2 || $[15] !== t3) {
    t4 = <>{t2}{afterLuckyLottoGroup}{t3}{gateModals}{cpcGateModals}</>;
    $[11] = afterLuckyLottoGroup;
    $[12] = cpcGateModals;
    $[13] = gateModals;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};

//* 서버 프리페치가 실패했을 때만 노출되는 폴백 — 한 섹션 분량의 레이아웃 공간만 예약한다.
export const BenefitMenuSectionFallback = t0 => {
  const $ = _c(6);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(BENEFIT_SECTION_CLASS_NAME, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton typo="body3" className="w-96 rounded-xsmall" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ul className="flex flex-col">{[0, 1, 2].map(_temp4)}</ul>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1) {
    t4 = <section className={t1}>{t2}{t3}</section>;
    $[4] = t1;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
function _temp(item) {
  return !isLuckyTicketAdItem(item);
}
function _temp2(group_0) {
  return group_0.title === LUCKY_LOTTO_GROUP_TITLE;
}
function _temp3(group_1) {
  return group_1.title !== LUCKY_LOTTO_GROUP_TITLE;
}
function _temp4(row) {
  return <li key={row} className="flex items-center gap-16 py-12"><Skeleton className="size-40 rounded-small" /><div className="flex flex-col items-start"><Skeleton typo="body4" className="w-120 rounded-xsmall" /><Skeleton typo="caption1" className="w-160 rounded-xsmall" /></div></li>;
}